/**
 * Combined pursuit and saccades — the third exercise.
 *
 * WHAT IT IS
 * ----------
 * One path, travelled back and forth. Each direction of travel uses a
 * different oculomotor system:
 *
 *     left  ->  right    the target GLIDES      (smooth pursuit)
 *     right ->  left     the target JUMPS       (saccades)
 *
 * and back again, indefinitely, until the prescribed time is up. Which system
 * gets the first leg is the doctor's choice, and it is the only control this
 * exercise adds that its two parents do not already have.
 *
 * WHY IT IS ITS OWN MODULE
 * ------------------------
 * docs/ADDING_AN_EXERCISE.md justifies a new module when the TASK LOGIC
 * differs. It does here, and not merely by being two tasks bolted together:
 * the patient must switch oculomotor systems on a cue that is itself the
 * stimulus, repeatedly, without warning. That switching cost is the point of
 * the exercise and neither parent measures it — pursuit never asks for a
 * saccade, and saccades never asks for pursuit.
 *
 * THE PATIENT DOES NOTHING WITH THEIR HANDS — changed 2026-08-23.
 *
 * Until now the patient held a pointer on the target through the gliding legs
 * and clicked each station on the jumping legs, which produced tracking error,
 * latency, accuracy and variability from one run. All of it is gone, here and
 * in the three other oculomotor exercises. The objection was not that the
 * numbers were impure but that asking for the hand CHANGED WHAT THE EYES WERE
 * DOING — attention moved to the cursor, a finger on a touchscreen covered the
 * target, and the click imposed a reaction-time task on top of a gaze task. In
 * tandem stance, a prescribed progression, it added motor demand to a patient
 * already unsteady.
 *
 * The switching cost this exercise exists to train is therefore no longer
 * measured, only trained. That is accepted: the platform trains and tracks, it
 * does not assess (§11), and the stimulus still forces the switch whether or
 * not anyone is counting. Compliance and the symptom rating the patient taps
 * between exercises carry the record. See DECISIONS.md 2026-08-23.
 *
 * DELIBERATELY NARROWER THAN ITS PARENTS
 * --------------------------------------
 * Only the straight paths are offered — horizontal, vertical and the two
 * diagonals. Circular, figure-of-eight and the cross and box patterns have no
 * "opposite direction" that could carry the second system, so they are absent
 * rather than present and meaningless. Back and forth is likewise not a
 * setting: it is the entire premise.
 */
import { degToPx } from '../types.js';
// =============================================================================
// Clinical colour choices — the same palettes as pursuit and saccades. Owned
// here rather than imported: a cartridge carries its own parameter surface.
// =============================================================================
const TARGET_COLOURS = [
    { value: '#f0f0f0', label: 'White' },
    { value: '#e02020', label: 'Red' },
    { value: '#2f6fe0', label: 'Blue' },
    { value: '#24a148', label: 'Green' },
    { value: '#f07800', label: 'Orange' },
    { value: '#8a3ffc', label: 'Purple' },
    { value: '#000000', label: 'Black' },
];
const BACKGROUND_COLOURS = [
    { value: '#14161a', label: 'Dark (low stimulus)' },
    { value: '#000000', label: 'Black' },
    { value: '#808080', label: 'Grey' },
    { value: '#ffffff', label: 'White' },
    { value: '#b00020', label: 'Red' },
    { value: '#1a3f8f', label: 'Blue' },
    { value: '#e8c400', label: 'Yellow' },
    { value: '#5b2a86', label: 'Purple' },
];
const CROSSHAIR_COMPLEMENT = {
    '#f0f0f0': '#e02020',
    '#e02020': '#f0f0f0',
    '#2f6fe0': '#f0b000',
    '#24a148': '#e0208a',
    '#f07800': '#2f6fe0',
    '#8a3ffc': '#c8e020',
    '#000000': '#f0f0f0',
};
/** Straight paths only — see the header note on why. */
const PATHS = {
    horizontal: { from: { x: -1, y: 0 }, to: { x: 1, y: 0 } },
    vertical: { from: { x: 0, y: -1 }, to: { x: 0, y: 1 } },
    diagonal_up: {
        from: { x: -1, y: -1 },
        to: { x: 1, y: 1 },
        fitAspect: true,
        mirrorOnReverse: true,
    },
    diagonal_down: {
        from: { x: -1, y: 1 },
        to: { x: 1, y: -1 },
        fitAspect: true,
        mirrorOnReverse: true,
    },
};
/** Assumed canvas shape for validation, which runs with no canvas present. */
const ASSUMED_ASPECT = 9 / 16;
function lerp(a, b, t) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
/** Angular gap between neighbouring saccade stations, in degrees. */
function stationSpacingDeg(pathKey, amplitudeDeg, stops) {
    const spec = PATHS[pathKey] ?? PATHS.horizontal;
    const yScale = spec.fitAspect ? ASSUMED_ASPECT : 1;
    const span = Math.hypot(spec.to.x - spec.from.x, (spec.to.y - spec.from.y) * yScale) * amplitudeDeg;
    return span / (Math.max(2, Math.round(stops)) - 1);
}
export const pursuitSaccadeCombo = {
    definition: {
        id: 'pursuit_saccade_combo',
        // Bumped to 2 on 2026-08-23 when the hand response was removed. Results
        // recorded under version 1 carry tracking error, latency, accuracy and
        // variability; from version 2 there is only completion. The tag is what
        // stops a chart showing that as a patient falling off a cliff.
        version: 2,
        name: 'Combined pursuit and saccades',
        category: 'oculomotor',
        clinicalDescription: 'One straight path travelled back and forth, with a different oculomotor ' +
            'system in each direction: the target glides one way and jumps the other. ' +
            'The patient must switch between pursuit and saccade on a cue that is the ' +
            'stimulus itself, repeatedly. The patient makes no response — nothing is ' +
            'clicked — so this trains and is not scored.',
        patientInstruction: 'Keep your eyes on the dot at all times. Travelling one way it will glide ' +
            'smoothly — follow it. Coming back it will jump from place to place — ' +
            'look at each new position as it lands. Move your eyes, not your head. ' +
            'There is nothing to click. If symptoms start to build, press the ' +
            'spacebar to stop — stopping early is the right call.',
        params: {
            path: {
                kind: 'enum',
                label: 'Path',
                help: 'Straight paths only. A closed shape has no opposite direction to ' +
                    'carry the second half of the exercise.',
                options: [
                    { value: 'horizontal', label: 'Horizontal' },
                    { value: 'vertical', label: 'Vertical' },
                    { value: 'diagonal_up', label: 'Diagonal — rising' },
                    { value: 'diagonal_down', label: 'Diagonal — falling' },
                ],
                default: 'horizontal',
            },
            travelDirection: {
                kind: 'enum',
                label: 'Which way',
                help: 'Which end the first leg starts from, and therefore which direction ' +
                    'each system runs in. On a vertical path this reads as upward or ' +
                    'downward.',
                options: [
                    { value: 'forward', label: 'Rightward' },
                    { value: 'reverse', label: 'Leftward' },
                ],
                default: 'forward',
            },
            startWith: {
                kind: 'enum',
                label: 'First leg',
                help: 'Which system takes the outward leg. On a horizontal path, Pursuit ' +
                    'means the target glides left to right and jumps on the way back; ' +
                    'Saccades means the reverse.',
                options: [
                    { value: 'pursuit', label: 'Pursuit' },
                    { value: 'saccades', label: 'Saccades' },
                ],
                default: 'pursuit',
            },
            amplitudeDeg: {
                kind: 'number',
                label: 'Amplitude',
                help: 'Maximum distance from centre, in degrees of visual angle. The path ' +
                    'runs twice this end to end.',
                min: 2,
                max: 25,
                step: 1,
                default: 13,
                unit: 'deg',
                fromCentre: true,
            },
            durationSec: {
                kind: 'number',
                label: 'Duration',
                help: 'How long the exercise runs. Legs keep alternating until the time is ' +
                    'up; a leg in progress at the end is cut short rather than overrunning.',
                min: 30,
                max: 600,
                step: 30,
                default: 60,
                unit: 's',
                displayUnit: { unit: 'min', factor: 60 },
            },
            speedDegPerSec: {
                kind: 'number',
                label: 'Pursuit speed',
                help: 'Constant target velocity on the gliding legs. Smooth pursuit ' +
                    'typically breaks into catch-up saccades above roughly 30–40 deg/s.',
                min: 2,
                max: 60,
                step: 1,
                default: 10,
                unit: 'deg/s',
            },
            stops: {
                kind: 'number',
                label: 'Stops per saccade leg',
                help: 'How many places the target lands on the jumping legs, spaced equally ' +
                    'with one at each end. Two is a single jump end to end; five makes ' +
                    'four smaller ones.',
                min: 2,
                max: 9,
                step: 1,
                default: 5,
                unit: 'count',
            },
            holdSec: {
                kind: 'number',
                label: 'Time on target',
                help: 'How long the target rests at each station on the jumping legs. This ' +
                    'sets the pace, so the same prescription is the same dose for every ' +
                    'patient.',
                min: 0.5,
                max: 6,
                step: 0.25,
                default: 1,
                unit: 's',
            },
            randomTiming: {
                kind: 'boolean',
                label: 'Unpredictable timing',
                help: 'Varies the rest at each station so the moment of the jump cannot be ' +
                    'anticipated. Isolates genuine reaction time.',
                default: false,
            },
            targetSizeDeg: {
                kind: 'number',
                label: 'Target size',
                help: 'Diameter in degrees of visual angle. Also sets how precisely the ' +
                    'patient must land on the jumping legs.',
                min: 0.5,
                max: 4,
                step: 0.5,
                default: 1,
                unit: 'deg',
            },
            targetColor: {
                kind: 'enum',
                label: 'Target colour',
                help: 'A clinical parameter, not a branding choice. Lower contrast against ' +
                    'the background for photophobic patients.',
                options: TARGET_COLOURS,
                default: '#f0f0f0',
            },
            crosshair: {
                kind: 'boolean',
                label: 'Crosshair on target',
                help: 'Draws fine lines inside the target to sharpen the fixation point.',
                default: true,
            },
            backgroundColor: {
                kind: 'enum',
                label: 'Background',
                help: 'The exercise canvas. Darker surfaces are lower stimulus for ' +
                    'photophobic patients; white is the brightest.',
                options: BACKGROUND_COLOURS,
                default: '#808080',
            },
            backgroundTexture: {
                kind: 'boolean',
                label: 'Textured background',
                help: 'Adds a faint depth texture behind the target.',
                default: true,
            },
        },
        /**
         * Completion only. With no patient response there is nothing else this
         * exercise can honestly report — see the header.
         */
        metrics: ['completion_pct'],
        decay: [],
        decayNotMonitored: 'Takes no patient response, so it emits no performance metric for the ' +
            'monitor to read. Tracking with a cursor and clicking the stations ' +
            'changed what the patient’s eyes were doing, which is the therapy. ' +
            'Symptoms and compliance carry the record. Changed 2026-08-23.',
        allowedPositions: ['seated', 'standing', 'tandem'],
        /** Empty: the patient watches. The only control is the host's stop. */
        allowedInputModes: [],
        involvesHeadMovement: false,
        involvesEyesClosed: false,
        /** The parameter the safety system shortens when reducing a dose. */
        durationParam: 'durationSec',
        estimateDurationSec(params) {
            return Math.round(Number(params.durationSec) + 10); // + instruction screen
        },
        validate(params) {
            const messages = [];
            const amplitude = Number(params.amplitudeDeg);
            const targetSize = Number(params.targetSizeDeg);
            const stops = Number(params.stops);
            const spacing = stationSpacingDeg(String(params.path), amplitude, stops);
            if (targetSize >= spacing) {
                messages.push({
                    severity: 'blocking',
                    param: 'targetSizeDeg',
                    message: `Targets would overlap on the jumping legs: neighbouring stops are ` +
                        `${spacing.toFixed(1)} degrees apart and the target is ${targetSize.toFixed(1)} ` +
                        'degrees across. Reduce the target size or the stops, or increase the amplitude.',
                });
            }
            return messages;
        },
    },
    // ===========================================================================
    // The run half — browser only. Nothing above this line touches the DOM.
    // ===========================================================================
    run(ctx) {
        const { params, canvas } = ctx;
        const requestedAmplitudeDeg = Number(params.amplitudeDeg);
        const durationSec = Number(params.durationSec);
        const speedDegPerSec = Number(params.speedDegPerSec);
        const stopCount = Math.max(2, Math.round(Number(params.stops)));
        const holdSec = Number(params.holdSec);
        const randomTiming = params.randomTiming === true;
        const targetSizeDeg = Number(params.targetSizeDeg);
        const targetColor = String(params.targetColor);
        const crosshairColor = CROSSHAIR_COMPLEMENT[targetColor] ?? '#e02020';
        const backgroundColor = String(params.backgroundColor);
        const crosshair = params.crosshair === true;
        const textured = params.backgroundTexture === true;
        /** Whether the OUTWARD leg glides. The return leg always does the other. */
        const outwardIsPursuit = String(params.startWith) !== 'saccades';
        /** Whether the outward leg runs left-to-right (or bottom-to-top). */
        const rightward = String(params.travelDirection) !== 'reverse';
        const pathSpec = PATHS[String(params.path)] ?? PATHS.horizontal;
        const surface = canvas.getContext('2d');
        let amplitudeDeg = requestedAmplitudeDeg;
        /** The two ends of the path, in degrees from centre. */
        let endA = { x: 0, y: 0 };
        let endB = { x: 0, y: 0 };
        let pathLengthDeg = 0;
        let texture = null;
        // Cached canvas box. Never measure the DOM inside the draw loop.
        let box = { width: 0, height: 0 };
        let devicePixelScale = 1;
        let resizeObserver = null;
        function measure() {
            const rect = canvas.getBoundingClientRect();
            box = { width: rect.width, height: rect.height };
            devicePixelScale = rect.width > 0 ? canvas.width / rect.width : 1;
        }
        function resolveGeometry() {
            measure();
            const radiusPx = degToPx(targetSizeDeg, ctx.pxPerDegree) / 2;
            const aspect = box.width > 0 ? box.height / box.width : 1;
            const yScale = pathSpec.fitAspect ? aspect : 1;
            const reachX = Math.max(Math.abs(pathSpec.from.x), Math.abs(pathSpec.to.x));
            const reachY = Math.max(Math.abs(pathSpec.from.y), Math.abs(pathSpec.to.y)) * yScale;
            const maxAmplitudeFor = (reach, halfPx) => reach > 0 ? Math.max(halfPx - radiusPx, 0) / ctx.pxPerDegree / reach : Infinity;
            amplitudeDeg = Math.min(requestedAmplitudeDeg, maxAmplitudeFor(reachX, box.width / 2), maxAmplitudeFor(reachY, box.height / 2));
            // What this screen can actually show, which is not always what was
            // prescribed. Reported on every resolve, including after a resize, so the
            // record describes the run rather than the intention.
            ctx.reportDelivered({ amplitudeDeg: Math.round(amplitudeDeg * 100) / 100 });
            // "Which way" swaps the ends, so the first leg starts from the other
            // side. Combined with First leg, that covers all four arrangements:
            // glide-out-rightward, glide-out-leftward, jump-out-rightward,
            // jump-out-leftward.
            //
            // The diagonals are also mirrored when running leftward, so a falling
            // path still falls rather than becoming a rising one under a falling
            // label. Vertical is not mirrored — its name claims nothing, so leftward
            // simply means downward.
            const mirror = pathSpec.mirrorOnReverse && !rightward ? -1 : 1;
            const yS = yScale * mirror;
            const start = rightward ? pathSpec.from : pathSpec.to;
            const end = rightward ? pathSpec.to : pathSpec.from;
            endA = { x: start.x * amplitudeDeg, y: start.y * yS * amplitudeDeg };
            endB = { x: end.x * amplitudeDeg, y: end.y * yS * amplitudeDeg };
            pathLengthDeg = Math.hypot(endB.x - endA.x, endB.y - endA.y);
        }
        function buildTexture() {
            if (!surface || !textured)
                return null;
            const tile = document.createElement('canvas');
            tile.width = 64;
            tile.height = 64;
            const tileSurface = tile.getContext('2d');
            if (!tileSurface)
                return null;
            tileSurface.fillStyle = backgroundColor;
            tileSurface.fillRect(0, 0, 64, 64);
            tileSurface.fillStyle = 'rgba(128,128,128,0.14)';
            for (let x = 0; x < 64; x += 8) {
                for (let y = 0; y < 64; y += 8) {
                    const jitterX = ((x * 7 + y * 13) % 5) - 2;
                    const jitterY = ((x * 11 + y * 5) % 5) - 2;
                    tileSurface.beginPath();
                    tileSurface.arc(x + jitterX, y + jitterY, 1.2, 0, Math.PI * 2);
                    tileSurface.fill();
                }
            }
            return surface.createPattern(tile, 'repeat');
        }
        let frame = 0;
        let stopped = false;
        let lastFrameAt = 0;
        let runningSec = 0;
        // --- leg state -----------------------------------------------------------
        let legIndex = -1;
        let legMode = 'pursuit';
        let legFrom = { x: 0, y: 0 };
        let legTo = { x: 0, y: 0 };
        let legStartedAt = 0;
        let legDurationSec = 0;
        /** Where the target is right now, in degrees from centre. */
        let position = { x: 0, y: 0 };
        // --- saccade-leg stations ------------------------------------------------
        let stationIndex = 0;
        let stationExpiresAt = 0;
        function stationAt(index) {
            return lerp(legFrom, legTo, index / (stopCount - 1));
        }
        function holdFor() {
            return holdSec + (randomTiming ? Math.random() * holdSec : 0);
        }
        function toScreen(p) {
            return {
                x: box.width / 2 + degToPx(p.x, ctx.pxPerDegree),
                y: box.height / 2 - degToPx(p.y, ctx.pxPerDegree),
            };
        }
        function beginLeg() {
            legIndex += 1;
            const outward = legIndex % 2 === 0;
            legFrom = outward ? endA : endB;
            legTo = outward ? endB : endA;
            legMode = outward === outwardIsPursuit ? 'pursuit' : 'saccade';
            legStartedAt = runningSec;
            if (legMode === 'pursuit') {
                legDurationSec = speedDegPerSec > 0 ? pathLengthDeg / speedDegPerSec : 0;
                position = legFrom;
            }
            else {
                // Station 0 is where the previous leg left the target, so it is a rest
                // rather than a trial. Trials are stations 1 upward.
                // Station 0 is where the previous leg left the target, so it is a
                // rest rather than a new jump. The jumps are stations 1 upward.
                stationIndex = 0;
                position = stationAt(0);
                stationExpiresAt = runningSec + holdFor();
            }
        }
        function advanceSaccadeLeg() {
            // Returns true when the leg is finished.
            if (runningSec < stationExpiresAt)
                return false;
            if (stationIndex >= stopCount - 1)
                return true;
            stationIndex += 1;
            position = stationAt(stationIndex);
            stationExpiresAt = runningSec + holdFor();
            return false;
        }
        function emitCompletion() {
            ctx.emit({
                t: runningSec * 1000,
                metric: 'completion_pct',
                value: Math.min(100, (runningSec / durationSec) * 100),
                detail: { legsCompleted: Math.max(legIndex, 0) },
            });
        }
        function finishRun(reason) {
            emitCompletion();
            ctx.finish(reason);
        }
        function draw() {
            if (stopped || !surface)
                return;
            const nowMs = ctx.now();
            const deltaSec = Math.min((nowMs - lastFrameAt) / 1000, 0.1);
            lastFrameAt = nowMs;
            runningSec += deltaSec;
            // --- advance the current leg -----------------------------------------
            if (legMode === 'pursuit') {
                const elapsed = runningSec - legStartedAt;
                if (legDurationSec <= 0 || elapsed >= legDurationSec) {
                    position = legTo;
                    beginLeg();
                }
                else {
                    position = lerp(legFrom, legTo, elapsed / legDurationSec);
                }
            }
            else if (advanceSaccadeLeg()) {
                beginLeg();
            }
            if (runningSec >= durationSec) {
                finishRun('completed');
                return;
            }
            // --- render -----------------------------------------------------------
            surface.setTransform(devicePixelScale, 0, 0, devicePixelScale, 0, 0);
            surface.fillStyle = backgroundColor;
            surface.fillRect(0, 0, box.width, box.height);
            if (texture) {
                surface.fillStyle = texture;
                surface.fillRect(0, 0, box.width, box.height);
            }
            const centre = toScreen(position);
            const radiusPx = Math.max(degToPx(targetSizeDeg, ctx.pxPerDegree) / 2, 1);
            surface.beginPath();
            surface.arc(centre.x, centre.y, radiusPx, 0, Math.PI * 2);
            surface.fillStyle = targetColor;
            surface.fill();
            if (crosshair) {
                surface.save();
                surface.beginPath();
                surface.arc(centre.x, centre.y, radiusPx, 0, Math.PI * 2);
                surface.clip();
                surface.strokeStyle = crosshairColor;
                surface.lineWidth = Math.max(radiusPx / 5, 1);
                surface.beginPath();
                surface.moveTo(centre.x - radiusPx, centre.y);
                surface.lineTo(centre.x + radiusPx, centre.y);
                surface.moveTo(centre.x, centre.y - radiusPx);
                surface.lineTo(centre.x, centre.y + radiusPx);
                surface.stroke();
                surface.restore();
            }
            // --- sample the gliding legs ------------------------------------------
            frame = requestAnimationFrame(draw);
        }
        return {
            start() {
                resolveGeometry();
                texture = buildTexture();
                lastFrameAt = ctx.now();
                if (typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => measure());
                    resizeObserver.observe(canvas);
                }
                beginLeg();
                frame = requestAnimationFrame(draw);
            },
            /** Instant and idempotent (rule 5). May be called twice. */
            stop(reason) {
                if (stopped)
                    return;
                stopped = true;
                cancelAnimationFrame(frame);
                resizeObserver?.disconnect();
                resizeObserver = null;
                if (reason !== 'completed') {
                    emitCompletion();
                }
            },
        };
    },
};
//# sourceMappingURL=pursuit-saccade-combo.js.map